import { getAuth, onIdTokenChanged, User } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { ORGANIZATIONS_PAGE } from 'src/api/requests';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { getPayloadJWT, isJWT } from 'src/utils/jwt';
import { Loading } from '../Loading';

/**
 * OrganizationAccessGuard validates that the user has access to the organization
 * specified in their JWT token. This guard runs early in the route hierarchy
 * to ensure that by the time we reach other guards and components, we can trust
 * that the organization in the token is valid.
 *
 * If the organization is invalid (user removed, organization deleted, etc.),
 * the user is redirected to the organizations page to select/create an organization.
 */
export const OrganizationAccessGuard = () => {
  const auth = getAuth();
  const { get } = useAuthedRequest();
  const { serviceKey } = useServiceMode();
  const [isValidating, setIsValidating] = useState(false);
  const [hasValidOrganization, setHasValidOrganization] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user: User | null) => {
      setIsValidating(true);

      try {
        if (!user) {
          setHasValidOrganization(false);
          return;
        }

        const token = await user.getIdToken();

        if (!token || !isJWT(token)) {
          setHasValidOrganization(false);
          return;
        }

        if (serviceKey) {
          setHasValidOrganization(true);
          return;
        }

        const decoded = getPayloadJWT(token);

        if (!decoded.organization?.id) {
          setHasValidOrganization(false);
          return;
        }

        try {
          await get(`/users/me`);
          setHasValidOrganization(true);
        } catch {
          setHasValidOrganization(false);
        }
      } catch {
        setHasValidOrganization(false);
      } finally {
        setIsValidating(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [auth, get, serviceKey]);

  if (isValidating || hasValidOrganization === null) {
    return <Loading className="p-4" />;
  }

  if (!hasValidOrganization) {
    return <Navigate to={ORGANIZATIONS_PAGE} replace />;
  }

  return <Outlet />;
};
